import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  task_assigned:   { bg: "bg-blue-50",    dot: "bg-blue-500" },
  task_due:        { bg: "bg-amber-50",   dot: "bg-amber-500" },
  task_overdue:    { bg: "bg-red-50",     dot: "bg-red-500" },
  comment_mention: { bg: "bg-purple-50",  dot: "bg-purple-500" },
  phase_completed: { bg: "bg-emerald-50", dot: "bg-emerald-500" },
  document_uploaded: { bg: "bg-blue-50", dot: "bg-blue-400" },
  broadcast:       { bg: "bg-slate-50",  dot: "bg-slate-500" },
  default:         { bg: "bg-slate-50",  dot: "bg-slate-400" },
};

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchNotifications(); }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`);
      setNotifications(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      setNotifications(p => p.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) { console.error(err); }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`);
      setNotifications(p => p.map(n => ({ ...n, is_read: true })));
      toast.success("All marked as read");
    } catch (err) { console.error(err); }
  };

  const deleteNotif = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      setNotifications(p => p.filter(n => n.id !== id));
    } catch (err) { console.error(err); }
  };

  const unread = notifications.filter(n => !n.is_read).length;

  if (loading) return <div className="p-8 text-[#64748B]">Loading notifications...</div>;

  return (
    <div className="p-6 max-w-2xl animate-fade-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-bold text-2xl text-[#0A1128]">Notifications</h1>
          {unread > 0 && <p className="text-sm text-[#64748B]">{unread} unread</p>}
        </div>
        {unread > 0 && (
          <button onClick={markAllRead} className="flex items-center gap-2 text-sm text-[#FFB703] hover:text-[#E8A200] font-medium" data-testid="mark-all-read-btn">
            <CheckCheck size={14} />Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center bg-white border border-[#E2E8F0] rounded">
          <Bell size={32} className="text-[#94A3B8] mb-3" />
          <p className="text-sm text-[#64748B]">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map(n => {
            const style = TYPE_STYLES[n.type] || TYPE_STYLES.default;
            return (
              <div
                key={n.id}
                onClick={() => !n.is_read && markRead(n.id)}
                className={`flex items-start gap-3 p-4 rounded border cursor-pointer transition-all ${n.is_read ? "bg-white border-[#E2E8F0] opacity-70" : `${style.bg} border-transparent`} hover:border-[#E2E8F0]`}
                data-testid={`notification-${n.id}`}
              >
                <div className={`w-2 h-2 rounded-full ${style.dot} mt-1.5 flex-shrink-0 ${n.is_read ? "opacity-30" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-[#0A1128]">{n.title}</div>
                  <div className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{n.message}</div>
                  <div className="text-[10px] text-[#94A3B8] mt-1">{new Date(n.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); deleteNotif(n.id); }}
                  className="text-[#CBD5E1] hover:text-red-400 transition-colors flex-shrink-0 p-1"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
